import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, Link } from 'react-router-dom';
import { api } from '../api/client';
import Breadcrumb from '../components/common/Breadcrumb';

const BADGE_ICONS = {
  megaphone: '&#x1F4E2;', target: '&#x1F3AF;', eye: '&#x1F441;', star: '&#x2B50;',
  trophy: '&#x1F3C6;', verified: '&#x2705;', search: '&#x1F50D;', shield: '&#x1F6E1;',
  thumbup: '&#x1F44D;', heart: '&#x2764;', flame: '&#x1F525;', money: '&#x1F4B0;',
  globe: '&#x1F30D;', rocket: '&#x1F680;', fire: '&#x1F525;', lightning: '&#x26A1;',
};

const CATEGORY_COLORS = {
  milestone: 'border-yellow-500/30 bg-yellow-500/10',
  quality: 'border-cyan-500/30 bg-cyan-500/10',
  social: 'border-pink-500/30 bg-pink-500/10',
  special: 'border-purple-500/30 bg-purple-500/10',
  engagement: 'border-orange-500/30 bg-orange-500/10',
};

export default function CommunityProfilePage() {
  const { t } = useTranslation();
  const { pseudo } = useParams();
  const [profile, setProfile] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.communityProfile(pseudo)
      .then(r => {
        setProfile(r.data);
        document.title = t('communityProfile.pageTitle', { pseudo: r.data.pseudo });
      })
      .catch(() => setError(t('communityProfile.notFoundError')));
    return () => { document.title = t('communityProfile.defaultTitle'); };
  }, [pseudo]);

  if (error) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-20 text-center">
        <div className="glass-card p-8">
          <h2 className="text-xl text-white font-semibold mb-2">{t('communityProfile.notFound')}</h2>
          <p className="text-gray-400">{error}</p>
          <Link to="/community" className="btn-secondary mt-4 inline-block">{t('communityProfile.backToCommunity')}</Link>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-20 text-center">
        <div className="w-8 h-8 border-2 border-safe border-t-transparent rounded-full animate-spin mx-auto" />
      </div>
    );
  }

  // XP progress to next level
  const THRESHOLDS = [0, 50, 200, 500, 1000, 2500, 5000, 10000, 25000];
  const currentThreshold = THRESHOLDS[profile.level - 1] || 0;
  const nextThreshold = THRESHOLDS[profile.level] || profile.xp;
  const xpProgress = nextThreshold > currentThreshold
    ? ((profile.xp - currentThreshold) / (nextThreshold - currentThreshold)) * 100
    : 100;

  return (
    <div className="max-w-4xl mx-auto px-4 py-10">
      <Breadcrumb items={[
        { label: t('breadcrumb.home'), to: '/' },
        { label: t('breadcrumb.community'), to: '/community' },
        { label: profile.pseudo },
      ]} />
      {/* Profile header */}
      <div className="glass-card p-8 mb-8">
        <div className="flex flex-col md:flex-row items-center gap-6">
          <div
            className="w-20 h-20 rounded-full flex items-center justify-center text-3xl font-bold text-white"
            style={{ backgroundColor: profile.avatar_color || '#00d4aa' }}
          >
            {profile.pseudo[0].toUpperCase()}
          </div>

          <div className="flex-1 text-center md:text-left">
            <div className="flex items-center gap-3 justify-center md:justify-start mb-1">
              <h1 className="text-2xl font-bold text-white">{profile.pseudo}</h1>
              <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-safe/10 text-safe border border-safe/30">
                Lvl {profile.level} - {profile.level_name}
              </span>
              <span className="text-gray-500 text-sm">#{profile.rank}</span>
            </div>

            {/* XP bar */}
            <div className="mt-2 max-w-xs mx-auto md:mx-0">
              <div className="flex justify-between text-xs text-gray-500 mb-1">
                <span>{profile.xp.toLocaleString()} XP</span>
                <span>{nextThreshold.toLocaleString()} XP</span>
              </div>
              <div className="h-2 bg-dark-surface rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-safe to-cyan-400 rounded-full transition-all"
                  style={{ width: `${Math.min(xpProgress, 100)}%` }}
                />
              </div>
            </div>

            <p className="text-gray-500 text-xs mt-2">
              {t('communityProfile.memberSince', { date: new Date(profile.created_at).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' }) })}
            </p>
          </div>
        </div>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <div className="glass-card p-4 text-center">
          <div className="text-2xl font-bold text-white">{profile.total_reports}</div>
          <div className="text-gray-500 text-xs">{t('communityProfile.reports')}</div>
        </div>
        <div className="glass-card p-4 text-center">
          <div className="text-2xl font-bold text-cyan-400">{profile.verified_reports}</div>
          <div className="text-gray-500 text-xs">{t('communityProfile.verified')}</div>
        </div>
        <div className="glass-card p-4 text-center">
          <div className="text-2xl font-bold text-pink-400">{profile.total_upvotes_received}</div>
          <div className="text-gray-500 text-xs">{t('communityProfile.upvotesReceived')}</div>
        </div>
        <div className="glass-card p-4 text-center">
          <div className="text-2xl font-bold text-orange-400">{profile.current_streak}j</div>
          <div className="text-gray-500 text-xs">{t('communityProfile.currentStreak', { max: profile.longest_streak })}</div>
        </div>
      </div>

      {/* Badges */}
      <div className="glass-card p-6 mb-8">
        <h2 className="text-lg font-bold text-white mb-4">
          {t('communityProfile.badges', { count: profile.badges_detail?.length || 0 })}
        </h2>
        {profile.badges_detail?.length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {profile.badges_detail.map((badge) => (
              <div
                key={badge.key}
                className={`rounded-lg border p-3 ${CATEGORY_COLORS[badge.category] || 'border-dark-border bg-dark-surface/30'}`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-lg" dangerouslySetInnerHTML={{ __html: BADGE_ICONS[badge.icon] || '&#x2B50;' }} />
                  <span className="text-white font-medium text-sm">{badge.label}</span>
                </div>
                <p className="text-gray-400 text-xs">{badge.description}</p>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-500 text-center py-4">{t('communityProfile.noBadges')}</p>
        )}
      </div>

      {/* Recent reports */}
      {profile.recent_reports?.length > 0 && (
        <div className="glass-card p-6">
          <h2 className="text-lg font-bold text-white mb-4">{t('communityProfile.recentReports')}</h2>
          <div className="space-y-2">
            {profile.recent_reports.map((report) => (
              <div key={report.id} className="flex items-center justify-between p-3 bg-dark-surface/30 rounded-lg">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-white text-sm font-mono">{report.sender_domain}</span>
                    <span className={`text-xs px-1.5 py-0.5 rounded ${
                      report.report_type === 'scam' ? 'bg-danger/20 text-danger' :
                      report.report_type === 'phishing' ? 'bg-orange-500/20 text-orange-400' :
                      'bg-gray-600/20 text-gray-400'
                    }`}>
                      {report.report_type}
                    </span>
                    {report.is_verified && <span className="text-xs text-cyan-400">{t('communityProfile.verifiedLabel')}</span>}
                  </div>
                  <p className="text-gray-500 text-xs truncate">{report.description}</p>
                </div>
                <div className="flex items-center gap-3 ml-4">
                  <span className="text-gray-500 text-xs">{new Date(report.created_at).toLocaleDateString('fr-FR')}</span>
                  <span className="text-gray-400 text-xs">{report.upvotes} {t('communityProfile.upvotes')}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="text-center mt-6">
        <Link to="/community" className="btn-secondary">{t('communityProfile.backToLeaderboard')}</Link>
      </div>
    </div>
  );
}
